/**
 * Utilities for analyzing annotation feedback patterns
 */

import type {
  AnnotationMachineComparison,
  DisagreementAnalysis,
  DisagreementHotspot,
  FeedbackTheme,
} from "@/metrics/registry/configuration/optimization/optimization"

import type { Annotation } from "@/types/api/Annotation"
import type { MetricOutputsByMetric } from "@/types/api/MetricOutputsByMetric"
import type { MetricRecordData } from "@/types/metrics/contracts"

import { compareAnnotationWithMachine } from "./machineComparison"

/**
 * Common keywords that indicate specific feedback themes
 */
const THEME_KEYWORDS: Record<string, string[]> = {
  "Edge case ambiguity": [
    "edge case",
    "edge-case",
    "unclear",
    "ambiguous",
    "boundary",
    "borderline",
    "gray area",
    "grey area",
    "not sure",
    "unsure",
  ],
  "Criteria clarity": [
    "criteria",
    "definition",
    "define",
    "what counts",
    "what is",
    "meaning",
    "interpret",
    "subjective",
    "vague",
  ],
  "Missing examples": [
    "example",
    "scenario",
    "case",
    "instance",
    "sample",
    "demonstrate",
  ],
  "Logic issues": [
    "contradict",
    "conflict",
    "and/or",
    "both",
    "either",
    "logic",
    "inconsistent",
  ],
  "Resolution ambiguity": [
    "resolve",
    "resolution",
    "solved",
    "fixed",
    "complete",
    "success",
    "fail",
  ],
}

/**
 * Extract explanation text from an annotation
 */
function getExplanationText(annotation: Annotation): string {
  const customExplanation = annotation.annotations?.custom_explanation || ""
  return customExplanation.toLowerCase()
}

/**
 * Detect themes/patterns across a set of annotations
 */
export function detectFeedbackThemes(
  annotations: Annotation[],
): FeedbackTheme[] {
  const themeMap = new Map<string, { count: number; annotationIds: string[] }>()

  for (const annotation of annotations) {
    const text = getExplanationText(annotation)
    if (!text) {
      continue
    }

    for (const [theme, keywords] of Object.entries(THEME_KEYWORDS)) {
      const hasKeyword = keywords.some((keyword) => text.includes(keyword))
      if (hasKeyword) {
        const existing = themeMap.get(theme) || { count: 0, annotationIds: [] }
        existing.count++
        existing.annotationIds.push(annotation.id)
        themeMap.set(theme, existing)
      }
    }
  }

  // Convert to array and sort by count
  const themes: FeedbackTheme[] = Array.from(themeMap.entries())
    .map(([theme, data]) => ({
      theme,
      count: data.count,
      annotationIds: data.annotationIds,
    }))
    .sort((a, b) => b.count - a.count)

  return themes
}

interface MachineDisagreementInput {
  annotations: Annotation[]
  metricOutputs: MetricOutputsByMetric[string]
  metric: MetricRecordData
}

export function calculateDisagreement({
  annotations,
  metricOutputs,
  metric,
}: MachineDisagreementInput): DisagreementAnalysis {
  let machineAgreements = 0
  let machineDisagreements = 0
  let comparableUnits = 0
  let nonComparableAnnotations = 0
  const comparisonsByAnnotationId: DisagreementAnalysis["comparisonsByAnnotationId"] =
    {}
  const hotspotKeywords = new Map<string, { agree: number; disagree: number }>()

  for (const annotation of annotations) {
    const comparison = compareAnnotationWithMachine(
      annotation,
      metricOutputs[annotation.simulation_output_id],
      metric,
    )
    comparisonsByAnnotationId[annotation.id] = comparison
    if (comparison.status === "not_comparable") {
      nonComparableAnnotations++
      continue
    }
    machineAgreements += comparison.matchingUnits
    machineDisagreements +=
      comparison.comparableUnits - comparison.matchingUnits
    comparableUnits += comparison.comparableUnits

    const text = getExplanationText(annotation)
    for (const [theme, keywords] of Object.entries(THEME_KEYWORDS)) {
      if (!keywords.some((keyword) => text.includes(keyword))) {
        continue
      }
      const existing = hotspotKeywords.get(theme) || {
        agree: 0,
        disagree: 0,
      }
      if (comparison.status === "agreement") {
        existing.agree++
      } else {
        existing.disagree++
      }
      hotspotKeywords.set(theme, existing)
    }
  }

  const hotspots: DisagreementHotspot[] = Array.from(hotspotKeywords.entries())
    .map(([condition, counts]) => ({
      condition,
      agreementRate:
        counts.agree + counts.disagree > 0
          ? counts.agree / (counts.agree + counts.disagree)
          : 1,
      annotationCount: counts.agree + counts.disagree,
    }))
    .filter((h) => h.agreementRate < 0.7)
    .sort((a, b) => a.agreementRate - b.agreementRate)

  return {
    machineDisagreementRate:
      comparableUnits > 0 ? machineDisagreements / comparableUnits : 0,
    machineAgreements,
    machineDisagreements,
    comparableUnits,
    nonComparableAnnotations,
    comparisonsByAnnotationId,
    hotspots,
    themes: detectFeedbackThemes(annotations),
  }
}

interface EnhancedFeedbackContextInput extends MachineDisagreementInput {
  currentUserId?: string
}

function describeComparison(comparison: AnnotationMachineComparison): string {
  if (comparison.status === "not_comparable") {
    return "Not comparable to an AI output"
  }
  if (comparison.comparableUnits > 1) {
    return `${comparison.status === "agreement" ? "Human-AI agreement" : "Human-AI disagreement"} (${comparison.matchingUnits}/${comparison.comparableUnits} criteria match)`
  }
  return comparison.status === "agreement"
    ? "Human-AI agreement"
    : "Human-AI disagreement"
}

export function buildEnhancedFeedbackContext({
  annotations,
  metricOutputs,
  metric,
  currentUserId,
}: EnhancedFeedbackContextInput): string {
  if (annotations.length === 0) {
    return "No feedback annotations available."
  }

  const analysis = calculateDisagreement({
    annotations,
    metricOutputs,
    metric,
  })
  const themes = analysis.themes

  let context = `FEEDBACK ANALYSIS SUMMARY
=========================

Total Annotations: ${annotations.length}

MACHINE-HUMAN COMPARISON:
Comparable Label Units: ${analysis.comparableUnits}
AI Matches: ${analysis.machineAgreements}
AI Disagreements: ${analysis.machineDisagreements}
Annotations Without Comparable AI Output: ${analysis.nonComparableAnnotations}
`

  if (analysis.machineDisagreementRate > 0.1) {
    const ratePercent = Math.round(analysis.machineDisagreementRate * 100)
    context += `
AI DISAGREEMENT ANALYSIS:
${
  analysis.machineDisagreementRate > 0.3
    ? `WARNING: High AI disagreement detected (${ratePercent}%)`
    : `Moderate AI disagreement (${ratePercent}%)`
}
`

    if (analysis.hotspots.length > 0) {
      context += `\nHotspot areas:\n`
      for (const hotspot of analysis.hotspots.slice(0, 3)) {
        const agreePercent = Math.round(hotspot.agreementRate * 100)
        context += `- "${hotspot.condition}": ${agreePercent}% human-AI agreement (${hotspot.annotationCount} annotations)\n`
      }
    }
  }

  if (themes.length > 0) {
    context += `\nRECURRING THEMES:\n`
    for (const theme of themes.slice(0, 5)) {
      context += `- ${theme.theme} (${theme.count} mentions)\n`
    }
  }

  context += `\nINDIVIDUAL REVIEWER FEEDBACK:\n`

  const sortedAnnotations = [...annotations].sort((a, b) => {
    if (currentUserId) {
      if (a.assignee === currentUserId && b.assignee !== currentUserId) {
        return -1
      }
      if (b.assignee === currentUserId && a.assignee !== currentUserId) {
        return 1
      }
    }
    return (
      new Date(b.last_updated_at).getTime() -
      new Date(a.last_updated_at).getTime()
    )
  })

  for (const annotation of sortedAnnotations.slice(0, 15)) {
    const comparison = analysis.comparisonsByAnnotationId[annotation.id]
    const explanation = annotation.annotations?.custom_explanation || "No notes"
    const isCurrentUser = currentUserId && annotation.assignee === currentUserId

    context += `${sortedAnnotations.indexOf(annotation) + 1}. ${describeComparison(comparison)}${isCurrentUser ? " (your annotation)" : ""}. Human [${comparison.humanValue}]; AI [${comparison.machineValue}]. Reviewer note: "${explanation}"\n`
  }

  if (annotations.length > 15) {
    context += `\n... and ${annotations.length - 15} more annotations\n`
  }

  context += `\nIMPROVEMENT FOCUS:
Based on this feedback, prioritize:
1. Correcting criteria behind actual human-AI disagreements
2. Clarifying ambiguity identified in reviewer notes
3. Preserving behavior where human and AI labels already agree
`

  return context
}
